import { collection, doc, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { Match, ParticipantPrediction, UserPrediction } from "@/modules/leagues/types/match";
import type { ScoringRule } from "@/modules/leagues/types/league";
import type { LeagueDetail } from "@/modules/leagues/types/league";
import { calculateMatchPoints } from "@/modules/scoring/ScoringEngine";
import { roundBonusRepository } from "@/config/dataSource";
import { ROUND_BONUS_MULTIPLIER } from "@/modules/leagues/types/roundBonus";

/**
 * Lógica compartilhada entre QUALQUER provedor de partidas (football-data.org
 * hoje, API-Football amanhã, o que vier depois) — trava de palpite,
 * pontuação provisória, e a lista de quem palpitou o quê. Extraído daqui
 * pra nenhum provedor novo reimplementar (e arriscar divergir em) essas
 * regras de segurança/pontuação, que são as mais sensíveis do app.
 */

/** Carrega TODOS os palpites do usuário logado para uma liga de uma vez. */
export async function loadUserPredictions(leagueId: string): Promise<Map<string, UserPrediction>> {
  const uid = auth.currentUser?.uid;
  const map = new Map<string, UserPrediction>();
  if (!uid) return map;

  const q = query(collection(db, "predictions"), where("leagueId", "==", leagueId), where("userId", "==", uid));
  const snap = await getDocs(q);
  snap.docs.forEach((d) => {
    const data = d.data() as {
      matchId: string;
      homeScore: number;
      awayScore: number;
      updatedAt?: string;
      pointsEarned?: number | null;
    };
    map.set(data.matchId, {
      homeScore: data.homeScore,
      awayScore: data.awayScore,
      submittedAt: data.updatedAt ?? new Date().toISOString(),
      pointsEarned: data.pointsEarned ?? null,
      isOfficial: (data.pointsEarned ?? null) !== null,
    });
  });
  return map;
}

export function applyPredictionsAndClubFilter(
  matches: Match[],
  predictions: Map<string, UserPrediction>,
  disabledClubIds: string[],
  scoringRules: ScoringRule[]
): Match[] {
  return matches
    .filter((m) => !disabledClubIds.includes(m.homeTeam.id) && !disabledClubIds.includes(m.awayTeam.id))
    .map((m) => {
      const rawPrediction = predictions.get(m.id) ?? null;
      const hasResult = m.homeScore !== null && m.awayScore !== null;
      const userPrediction: UserPrediction | null =
        rawPrediction && rawPrediction.pointsEarned === null && hasResult
          ? {
              ...rawPrediction,
              pointsEarned: calculateMatchPoints(
                {
                  predictedHome: rawPrediction.homeScore,
                  predictedAway: rawPrediction.awayScore,
                  actualHome: m.homeScore as number,
                  actualAway: m.awayScore as number,
                },
                scoringRules
              ),
              isOfficial: false,
            }
          : rawPrediction
            ? { ...rawPrediction, isOfficial: rawPrediction.pointsEarned !== null }
            : null;
      return {
        ...m,
        userPrediction,
        predictionsLocked: m.predictionsLocked || new Date(m.kickoff).getTime() <= Date.now(),
      };
    });
}

export function isPredictionLocked(match: Pick<Match, "status" | "kickoff">): boolean {
  return match.status !== "scheduled" || new Date(match.kickoff).getTime() <= Date.now();
}

// Janela máxima que uma partida pode ficar "provavelmente ao vivo" só por
// causa do horário, quando a API ainda não atualizou o status — cobre 90
// minutos normais + até 30 de prorrogação + pênaltis + margem de sobra.
// Bug real corrigido (relatado pelo usuário): sem este teto, uma partida
// de ONTEM que a API deixou presa em "SCHEDULED" (por qualquer motivo do
// lado deles) era classificada como "Ao vivo" HOJE, porque a checagem
// antiga só perguntava "já passou do horário?" sem limite de quanto tempo
// atrás. Isso NÃO se aplica ao bloqueio de palpite (isPredictionLocked
// acima) — uma vez começada, a partida deve continuar bloqueada pra
// sempre, não tem "teto" ali de propósito.
const MAX_HOURS_TO_ASSUME_STILL_LIVE = 4;

/** "Ao vivo" pra fins de EXIBIÇÃO (aba, boost do ranking, revelar
 * participantes) — diferente de `isPredictionLocked` (que nunca expira,
 * de propósito). Fora dessa janela, mesmo com status ainda "scheduled",
 * assume dado desatualizado — mais honesto tratar como "provavelmente
 * encerrada" do que continuar chamando de "ao vivo" indefinidamente. */
export function isLikelyStillLive(match: Pick<Match, "status" | "kickoff">): boolean {
  if (match.status === "live") return true;
  if (match.status !== "scheduled") return false;
  const hoursSinceKickoff = (Date.now() - new Date(match.kickoff).getTime()) / (60 * 60 * 1000);
  return hoursSinceKickoff >= 0 && hoursSinceKickoff <= MAX_HOURS_TO_ASSUME_STILL_LIVE;
}

/** Companheira de `isLikelyStillLive` — true quando o horário já passou
 * mas está FORA da janela de "provavelmente ao vivo" (ou seja, dado
 * travado/desatualizado, tratado como provavelmente encerrada). */
export function isStaleScheduledPastKickoff(match: Pick<Match, "status" | "kickoff">): boolean {
  if (match.status !== "scheduled") return false;
  const hoursSinceKickoff = (Date.now() - new Date(match.kickoff).getTime()) / (60 * 60 * 1000);
  return hoursSinceKickoff > MAX_HOURS_TO_ASSUME_STILL_LIVE;
}

export async function getParticipantPredictionsForMatch(
  leagueId: string,
  match: Match,
  league: LeagueDetail
): Promise<ParticipantPrediction[]> {
  // Continua sendo o pré-filtro certo pra não tentar buscar antes da hora
  // (evita uma leitura inútil) — mas quem garante a segurança de verdade
  // agora é o campo `participantsVisible` no próprio documento, gravado
  // pelo cron, não mais uma comparação de horário na hora da leitura.
  if (!isPredictionLocked(match)) return [];

  const [predictionsSnap, participantsSnap, roundBonuses] = await Promise.all([
    getDocs(
      query(
        collection(db, "predictions"),
        where("leagueId", "==", leagueId),
        where("matchId", "==", match.id),
        where("participantsVisible", "==", true)
      )
    ),
    getDocs(collection(db, "groups", leagueId, "participants")),
    roundBonusRepository.getRoundBonuses(leagueId, match.round),
  ]);

  const bonusForThisMatch = roundBonuses.find((b) => b.matchId === match.id)?.type ?? null;
  const exactScoreMultiplier = bonusForThisMatch ? ROUND_BONUS_MULTIPLIER[bonusForThisMatch] : 1;

  const nameByUid = new Map(
    participantsSnap.docs.map((d) => {
      const p = d.data() as { uid: string; nome: string; avatarUrl: string | null };
      return [p.uid, { nome: p.nome, avatarUrl: p.avatarUrl }];
    })
  );

  const hasResult = match.homeScore !== null && match.awayScore !== null;

  return predictionsSnap.docs.map((d) => {
    const data = d.data() as { userId: string; homeScore: number; awayScore: number; pointsEarned: number | null };
    const participant = nameByUid.get(data.userId);

    const pointsEarned =
      data.pointsEarned !== null
        ? data.pointsEarned
        : hasResult
          ? calculateMatchPoints(
              {
                predictedHome: data.homeScore,
                predictedAway: data.awayScore,
                actualHome: match.homeScore as number,
                actualAway: match.awayScore as number,
              },
              league.rules.rules,
              exactScoreMultiplier
            )
          : null;

    const acertouPlacar = hasResult && data.homeScore === match.homeScore && data.awayScore === match.awayScore;
    const acertouVencedor =
      hasResult &&
      Math.sign(data.homeScore - data.awayScore) === Math.sign((match.homeScore as number) - (match.awayScore as number));

    return {
      uid: data.userId,
      nome: participant?.nome ?? "Participante",
      avatarUrl: participant?.avatarUrl ?? null,
      homeScore: data.homeScore,
      awayScore: data.awayScore,
      pointsEarned,
      isOfficial: data.pointsEarned !== null,
      acertouPlacar,
      acertouVencedor,
    };
  });
}

export function predictionDocId(leagueId: string, matchId: string, uid: string): string {
  return `${leagueId}_${matchId}_${uid}`;
}

export function predictionDocRef(leagueId: string, matchId: string, uid: string) {
  return doc(db, "predictions", predictionDocId(leagueId, matchId, uid));
}
